import { createContext, type ReactNode, use } from 'react';
import {
  KeyboardAwareScrollView,
  KeyboardStickyView,
} from 'react-native-keyboard-controller';
import Animated, { FadeIn } from 'react-native-reanimated';
import { StyleSheet, withUnistyles } from 'react-native-unistyles';
import Box from '@/components/Unistyles/Box';
import Text from '@/components/Unistyles/Text';

type WelcomeStepFrameProps = {
  title: string;
  subtitle?: string;
  children?: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
};

const CONTENT_ANIMATION_MS = 320;
export const WELCOME_INPUT_BOTTOM_OFFSET = 200;

const UnistylesKeyboardStickyView = withUnistyles(KeyboardStickyView);
const WelcomeStepHeaderContext = createContext<ReactNode>(null);

export function WelcomeStepHeader({
  title,
  subtitle,
}: {
  title: string;
  subtitle?: string;
}) {
  return (
    <Box space="sm" style={style.header}>
      <Text size="3xl" bold center>
        {title}
      </Text>
      {subtitle ? (
        <Text size="lg" variant="typographySecondary" center>
          {subtitle}
        </Text>
      ) : null}
    </Box>
  );
}

export function WelcomeStepScrollContent({
  children,
}: {
  children: ReactNode;
}) {
  const header = use(WelcomeStepHeaderContext);

  return (
    <KeyboardAwareScrollView
      contentInsetAdjustmentBehavior="automatic"
      keyboardShouldPersistTaps="handled"
      bottomOffset={WELCOME_INPUT_BOTTOM_OFFSET}
      style={style.screenScroller}
      contentContainerStyle={style.screenScrollContent}
    >
      {header}
      {children}
    </KeyboardAwareScrollView>
  );
}

export default function WelcomeStepFrame({
  title,
  subtitle,
  children,
  footer,
  wide = false,
}: WelcomeStepFrameProps) {
  const header = <WelcomeStepHeader title={title} subtitle={subtitle} />;
  const shouldRenderHeaderInScroll = !wide && Boolean(children);
  const content = (
    <Animated.View
      entering={FadeIn.duration(CONTENT_ANIMATION_MS)}
      style={[style.animatedContent, wide ? style.animatedContentWide : null]}
    >
      {shouldRenderHeaderInScroll ? (
        <WelcomeStepHeaderContext value={header}>
          {children}
        </WelcomeStepHeaderContext>
      ) : (
        <>
          {header}
          {children}
        </>
      )}
    </Animated.View>
  );

  return (
    <Box flex>
      <Box style={[style.contentShell, wide ? style.contentShellWide : null]}>
        <Box style={style.contentArea}>{content}</Box>
      </Box>
      {footer ? (
        <UnistylesKeyboardStickyView
          uniProps={(_, rt) => ({
            offset: { closed: 0, opened: rt.insets.bottom },
          })}
        >
          <Box style={style.footer}>{footer}</Box>
        </UnistylesKeyboardStickyView>
      ) : null}
    </Box>
  );
}

const style = StyleSheet.create((theme) => ({
  animatedContent: {
    alignSelf: 'center',
    flex: 1,
    gap: theme.space('xl'),
    justifyContent: 'center',
    maxWidth: theme.size(132),
    minHeight: 0,
    width: '100%',
  },
  animatedContentWide: {
    alignSelf: 'stretch',
    flex: 1,
    maxWidth: '100%',
    minHeight: 0,
  },
  contentShell: {
    alignSelf: 'center',
    flex: 1,
    maxWidth: theme.size(176),
    paddingHorizontal: {
      base: theme.space('lg'),
      md: theme.space('4xl'),
    },
    width: '100%',
  },
  contentShellWide: {
    maxWidth: '100%',
    paddingHorizontal: {
      base: theme.space('sm'),
      md: theme.space('md'),
    },
  },
  footer: {
    backgroundColor: theme.colors.background,
    padding: theme.space('lg'),
  },
  contentArea: {
    flex: 1,
    minHeight: 0,
    paddingBottom: theme.space('md'),
    paddingTop: theme.space('md'),
    width: '100%',
  },
  header: {
    flexShrink: 0,
  },
  screenScrollContent: {
    flexGrow: 1,
    gap: theme.space('md'),
    justifyContent: 'center',
    paddingBottom: theme.space('lg'),
    paddingTop: theme.space('lg'),
  },
  screenScroller: {
    flexGrow: 1,
    flexShrink: 1,
    minHeight: 0,
    width: '100%',
  },
}));
